import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FolderOpen, Wand2, Scissors, Crop, Captions, SlidersHorizontal, Palette,
  LayoutTemplate, ClipboardCheck, ArrowLeft, Upload, Link2, Loader2,
  ChevronsUp, ChevronsDown, Play, Pause, Film, Video, CheckCircle2, Circle,
  Clapperboard, Layers, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { EditorToolSidebar } from '@/components/editor/EditorToolSidebar';
import { EditorMediaPanel } from '@/components/editor/EditorMediaPanel';
import { EditorClipGenerationPanel } from '@/components/editor/EditorClipGenerationPanel';
import { EditorClipList } from '@/components/editor/EditorClipList';
import { EditorClipTrimControls } from '@/components/editor/EditorClipTrimControls';
import { EditorCaptionsPanel } from '@/components/editor/EditorCaptionsPanel';
import { EditorBrandKitPanel } from '@/components/editor/EditorBrandKitPanel';
import { EditorAiToolsPanel } from '@/components/editor/EditorAiToolsPanel';
import { EditorReviewPanel } from '@/components/editor/EditorReviewPanel';
import { EditorClipReviewQueue } from '@/components/editor/EditorClipReviewQueue';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { EditorPlaceholderPanel } from '@/components/editor/EditorPlaceholderPanel';
import { ClipVariantGenerator } from '@/components/editor/ClipVariantGenerator';
import { platformLabel } from '@/lib/clipPlatformFormats';
import { StoryCreatePanel } from '@/components/editor/StoryCreatePanel';
import { StoryManagerPanel } from '@/components/editor/StoryManagerPanel';
import { DesignReviewPanel } from '@/components/editor/DesignReviewPanel';
import { fmtTime } from '@/components/editor/EditorTimelineRuler';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const CLIP_TOOL_CATEGORIES = [
  { key: 'media', label: 'Media', icon: FolderOpen },
  { key: 'generate', label: 'Generate', icon: Wand2 },
  { key: 'clips', label: 'Clips', icon: Scissors },
  { key: 'reframe', label: 'Reframe', icon: Crop },
  { key: 'captions', label: 'Captions', icon: Captions },
  { key: 'edit', label: 'Edit', icon: SlidersHorizontal },
  { key: 'brand', label: 'Brand Kit', icon: Palette },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'review', label: 'Review', icon: ClipboardCheck },
];
const CLIP_CATEGORY_LABELS = Object.fromEntries(CLIP_TOOL_CATEGORIES.map((c) => [c.key, c.label]));

/**
 * Clips — a dedicated generator/editor workspace for EditorClip records
 * (manual, marker-assisted, AI-assisted and multi-platform-variant
 * creation, then trim/caption/brand/reframe/review), reusing the Main
 * Editor's CapCut-style shell (icon rail + retractable panel + preview +
 * context Properties + resizable strip) and every existing clip
 * component/endpoint Main Editor's former "Clip Editor" dialog already
 * used — nothing here is a new rendering/generation engine.
 */
export default function ClipsWorkspace() {
  const { contentSourceId: currentSourceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emptyStateProjectId = searchParams.get('projectId');
  const returnTo = searchParams.get('returnTo');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [contentSource, setContentSource] = useState(null);
  const [siblingSources, setSiblingSources] = useState([]);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const [brandkits, setBrandkits] = useState([]);

  const [emptyStateVideos, setEmptyStateVideos] = useState([]);
  const [emptyStateVideosLoading, setEmptyStateVideosLoading] = useState(false);

  const projectRef = useRef(null);
  useEffect(() => { projectRef.current = project; }, [project]);
  const autosaveTimerRef = useRef(null);
  const pollTimersRef = useRef({});

  useEffect(() => {
    if (!currentSourceId) {
      setLoading(false);
      if (emptyStateProjectId) {
        setEmptyStateVideosLoading(true);
        fetch(`${API_URL}/api/projects/${emptyStateProjectId}`, { credentials: 'include' })
          .then((r) => r.json())
          .then((data) => setEmptyStateVideos((data.parent_assets || []).filter((a) => a.media_type === 'video')))
          .catch(() => {})
          .finally(() => setEmptyStateVideosLoading(false));
      }
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/editor/projects/source/${currentSourceId}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then((data) => {
        setProject(data.project);
        setContentSource(data.content_source);
        setSiblingSources(data.sibling_sources || []);
        setStreamUrl(data.stream_url || null);
        setStreamError(data.stream_error || null);
      })
      .catch(() => toast.error('Could not load this video for Clips'))
      .finally(() => setLoading(false));
  }, [currentSourceId, emptyStateProjectId]);

  useEffect(() => {
    const creatorId = contentSource?.creator_id;
    if (!creatorId) return;
    (async () => {
      try {
        const bkRes = await fetch(`${API_URL}/api/brandkits/creator/${creatorId}`, { credentials: 'include' });
        const creatorKits = bkRes.ok ? await bkRes.json() : [];
        let appKits = [];
        try {
          const appRes = await fetch(`${API_URL}/api/brandkits/app`, { credentials: 'include' });
          if (appRes.ok) appKits = await appRes.json();
        } catch { /* non-admin — app kits optional */ }
        setBrandkits([...creatorKits, ...appKits]);
      } catch { /* brand kits optional for Clips */ }
    })();
  }, [contentSource?.creator_id]);

  const [effectiveBrand, setEffectiveBrand] = useState(null);
  useEffect(() => {
    if (!project?.id) return;
    fetch(`${API_URL}/api/editor/projects/${project.id}/brand/effective`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setEffectiveBrand).catch(() => {});
  }, [project?.id]);
  const effectiveBrandKit = effectiveBrand?.brand_kit || null;

  // ── Master approval snapshot — read-only here. Clips never bypasses the
  // approval gate (generation below checks this), but never blocks entry
  // into the workspace or any tool browsing either. ──
  const [activeApprovalSnapshot, setActiveApprovalSnapshot] = useState(null);
  const fetchMasterApprovalState = useCallback(async () => {
    if (!project?.id) return;
    try {
      const apprRes = await fetch(`${API_URL}/api/editor/projects/${project.id}/master/approved`, { credentials: 'include' });
      if (apprRes.ok) setActiveApprovalSnapshot((await apprRes.json()).snapshot || null);
    } catch { /* non-critical */ }
  }, [project?.id]);
  useEffect(() => { fetchMasterApprovalState(); }, [fetchMasterApprovalState]);
  const masterApproved = project?.master_composition?.approval_status === 'approved';
  const approvedDuration = activeApprovalSnapshot?.composition_snapshot?.duration_seconds;

  // ── Generation (manual + AI) — identical endpoint/body Main Editor uses ──
  const [generatingClips, setGeneratingClips] = useState(false);
  const generateClips = async (payload) => {
    if (!project?.id) return;
    setGeneratingClips(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/generate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Generation failed');
      const data = await res.json();
      setProject((p) => ({ ...p, clips: [...(p.clips || []), ...(data.clips || [])] }));
      toast.success(`Generated ${data.clips.length} clip${data.clips.length === 1 ? '' : 's'}`);
      setToolCategory('clips'); setViewMode('category');
      return data.clips || [];
    } catch (err) {
      toast.error(err.message || 'Could not generate clips');
      return [];
    } finally { setGeneratingClips(false); }
  };
  const generateClipsFromSuggestions = async (suggestionIds, outputType) => {
    if (!project?.id) return;
    setGeneratingClips(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/generate-from-suggestions`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggestion_ids: suggestionIds, output_type: outputType }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Generation failed');
      const data = await res.json();
      setProject((p) => ({ ...p, clips: [...(p.clips || []), ...(data.clips || [])] }));
      toast.success(`Generated ${data.clips.length} clip${data.clips.length === 1 ? '' : 's'} from AI suggestions`);
      setToolCategory('clips'); setViewMode('category');
    } catch (err) { toast.error(err.message || 'Could not generate from suggestions'); }
    finally { setGeneratingClips(false); }
  };

  // ── Multi-platform variants (required Clips feature, section 6) — calls
  // the exact same generate endpoint once per selected platform so every
  // variant is a real, independently editable/approvable EditorClip;
  // "platform" + the shared variantGroupId are tracked only in the
  // localStorage map above since EditorClip has no such backend fields. ──
  const [generatingVariants, setGeneratingVariants] = useState(false);
  const generateVariants = async ({ assetType, platforms, start, end, aspect }) => {
    if (!project?.id || platforms.length === 0) return;
    setGeneratingVariants(true);
    const groupId = newId();
    const nextMeta = { ...variantMeta };
    try {
      for (const platform of platforms) {
        const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/generate`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            output_type: assetType === 'long_form' ? 'lf' : 'sf',
            target_aspect_ratio: aspect,
            ranges: [{ start_seconds: start, end_seconds: end, name: `Clip — ${platformLabel(platform)}` }],
          }),
        });
        if (!res.ok) { toast.error(`${platformLabel(platform)} variant failed — ${(await res.json().catch(() => ({}))).detail || 'server error'}`); continue; }
        const data = await res.json();
        (data.clips || []).forEach((c) => { nextMeta[c.id] = { variantGroupId: groupId, platform, assetType }; });
        setProject((p) => ({ ...p, clips: [...(p.clips || []), ...(data.clips || [])] }));
      }
      persistVariantMeta(nextMeta);
      toast.success(`Created ${platforms.length} platform variant${platforms.length === 1 ? '' : 's'}`);
      setToolCategory('clips'); setViewMode('category');
    } finally { setGeneratingVariants(false); }
  };

  const clips = project?.clips || [];

  // ── Clip selection + Properties auto-switch (mirrors Main Editor) ──
  const [selectedClipId, setSelectedClipId] = useState(null);
  const selectedClip = clips.find((c) => c.id === selectedClipId) || null;
  const [activeOutputType, setActiveOutputType] = useState('lf');
  const clipsOfActiveType = clips.filter((c) => c.output_type === activeOutputType).sort((a, b) => a.order - b.order);
  const allClipsSorted = [...clips].sort((a, b) => (a.output_type === b.output_type ? a.order - b.order : a.output_type.localeCompare(b.output_type)));
  const [toolCategory, setToolCategory] = useState('media');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('category');
  const [lastCategoryBeforeProperties, setLastCategoryBeforeProperties] = useState('media');
  const toolCategoryRef = useRef(toolCategory);
  useEffect(() => { toolCategoryRef.current = toolCategory; }, [toolCategory]);
  useEffect(() => {
    if (selectedClipId) {
      setViewMode((vm) => { if (vm !== 'properties') setLastCategoryBeforeProperties(toolCategoryRef.current); return 'properties'; });
    } else { setViewMode('category'); }
  }, [selectedClipId]);
  const handleSelectCategory = (key) => { setViewMode('category'); setToolCategory(key); };
  const handleBackFromProperties = () => { setViewMode('category'); setToolCategory(lastCategoryBeforeProperties); };

  // ── Resizable/collapsible clip strip — same drag-handle pattern as the
  // Main Editor timeline, applied to the ordered EditorClipList since
  // EditorClip has no tracks to show a multitrack timeline for. ──
  const [stripHeight, setStripHeight] = useState(220);
  const [stripCollapsed, setStripCollapsed] = useState(false);
  const stripResizeRef = useRef(null);
  const startStripResize = (e) => {
    const startY = e.clientY;
    const startHeight = stripHeight;
    stripResizeRef.current = { startY, startHeight };
    const onMove = (ev) => {
      const delta = stripResizeRef.current.startY - ev.clientY;
      setStripHeight(Math.min(420, Math.max(120, stripResizeRef.current.startHeight + delta)));
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Clip-local video preview (native <video>, not a new engine) — used
  // by trim/split controls for whichever clip is selected. ──
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const handleLoadedMetadata = () => { if (videoRef.current) setDuration(videoRef.current.duration || 0); };
  const handleTimeUpdate = () => { if (videoRef.current) setCurrentTime(videoRef.current.currentTime); };
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause(); else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };
  const seekTo = (t) => { if (videoRef.current) { videoRef.current.currentTime = t; setCurrentTime(t); } };
  const activeCaption = selectedClip && !selectedClip.master_composition_slice
    ? (selectedClip.captions || []).find((c) => (currentTime - selectedClip.in_point) >= c.start && (currentTime - selectedClip.in_point) <= c.end) || null
    : null;
  const effectiveDuration = project?.source_duration || duration || Math.max(60, ...clips.map((c) => c.out_point || 0), 0);

  const updateSelectedClip = (partial) => {
    if (!selectedClipId) return;
    applyClips(clips.map((c) => (c.id === selectedClipId ? { ...c, ...partial } : c)));
  };
  const renameClip = (clipId, name) => applyClips(clips.map((c) => (c.id === clipId ? { ...c, name } : c)));
  const removeClip = (clipId) => {
    applyClips(clips.filter((c) => c.id !== clipId));
    if (selectedClipId === clipId) setSelectedClipId(null);
    const nextMeta = { ...variantMeta }; delete nextMeta[clipId]; persistVariantMeta(nextMeta);
  };
  const reorderClip = (clipId, direction) => {
    const idx = clips.findIndex((c) => c.id === clipId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= clips.length) return;
    const next = [...clips];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    applyClips(next);
  };
  const splitSelectedClip = () => {
    if (!selectedClip || selectedClip.master_composition_slice) return;
    const splitLocal = currentTime - selectedClip.in_point;
    const clip1Captions = []; const clip2Captions = [];
    (selectedClip.captions || []).forEach((cap) => {
      if (cap.end <= splitLocal) clip1Captions.push(cap);
      else if (cap.start >= splitLocal) clip2Captions.push({ ...cap, start: cap.start - splitLocal, end: cap.end - splitLocal });
      else { clip1Captions.push({ ...cap, end: splitLocal }); clip2Captions.push({ ...cap, id: newId(), start: 0, end: cap.end - splitLocal }); }
    });
    const clip1 = { ...selectedClip, out_point: currentTime, captions: clip1Captions };
    const clip2 = {
      ...selectedClip, id: newId(), name: `${selectedClip.name} (2)`, in_point: currentTime,
      captions: clip2Captions, approved: false, render_job_id: null, render_status: 'idle', output_b2_key: null, render_error: null,
      order: selectedClip.order + 0.5,
    };
    const rest = clips.filter((c) => c.id !== selectedClip.id);
    const merged = [...rest, clip1, clip2].sort((a, b) => (a.output_type === b.output_type ? a.order - b.order : 0));
    applyClips(merged);
    setSelectedClipId(clip1.id);
    toast.success('Clip split at playhead');
  };

  const retrimMasterClip = async (clipId, startSeconds, endSeconds, targetAspectRatio) => {
    if (!project?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/retrim`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_seconds: startSeconds, end_seconds: endSeconds, target_aspect_ratio: targetAspectRatio }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Retrim failed');
      const data = await res.json();
      setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? data.clip : c)) }));
      toast.success('Clip range updated');
    } catch (err) { toast.error(err.message || 'Could not retrim this clip'); }
  };

  const [clipRangeDraft, setClipRangeDraft] = useState({ start: 0, end: 0 });
  useEffect(() => {
    if (selectedClip) setClipRangeDraft({ start: selectedClip.in_point ?? selectedClip.master_range_start_seconds ?? 0, end: selectedClip.out_point ?? selectedClip.master_range_end_seconds ?? 0 });
  }, [selectedClipId]);

  // ── Captions (per clip) — fields are clip-relative (`start`/`end`), the
  // exact same shape EditorCaptionsPanel and Main Editor's caption text
  // items share. ──
  const addCaption = (localTime) => {
    if (!selectedClip) return;
    const dur = selectedClip.out_point - selectedClip.in_point;
    const cap = { id: newId(), start: localTime, end: Math.min(localTime + 2, dur), text: '' };
    updateSelectedClip({ captions: [...(selectedClip.captions || []), cap].sort((a, b) => a.start - b.start) });
  };
  const updateCaption = (capId, partial) => {
    if (!selectedClip) return;
    updateSelectedClip({ captions: (selectedClip.captions || []).map((c) => (c.id === capId ? { ...c, ...partial } : c)) });
  };
  const removeCaption = (capId) => {
    if (!selectedClip) return;
    updateSelectedClip({ captions: (selectedClip.captions || []).filter((c) => c.id !== capId) });
  };

  // ── Brand Kit (per clip or batch to all of the same output_type) ──
  const applyBrandKit = (brandKitId) => updateSelectedClip({ brandkit_id: brandKitId });
  const applyBrandKitToAllOfType = (brandKitId) => {
    if (!selectedClip) return;
    applyClips(clips.map((c) => (c.output_type === selectedClip.output_type ? { ...c, brandkit_id: brandKitId } : c)));
    toast.success(`Applied to all ${selectedClip.output_type === 'lf' ? 'long-form' : 'short-form'} clips`);
  };

  // ── AI highlight suggestions — reuses the SAME ai_edit_suggestions /
  // ai-analysis endpoints as Main Editor's "Best Moments" capability (not
  // the separate, UI-only ai_suggestions/EditorAiPanel path, which has no
  // generation wiring). Honesty note carried over from Main Editor's own
  // code comment: no AI capability can reach "completed" without a
  // connected provider today, so a request never actually returns
  // suggestions yet — this is a known, pre-existing limitation, not
  // something this task introduced or can fix within Level 1/2. ──
  const requestAiAnalysis = async (capability, contentSourceId, params = {}) => {
    if (!project) return;
    const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/ai-analysis/request`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_source_id: contentSourceId, capability, params }),
    });
    if (!res.ok) throw new Error('AI analysis request failed');
    return res.json();
  };
  const setAiEditSuggestionStatus = async (suggestionId, status) => {
    const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/ai-edits/${suggestionId}/${status === 'accepted' ? 'accept' : 'reject'}`, { method: 'POST', credentials: 'include' });
    if (!res.ok) return;
    const updated = await res.json();
    const next = (project.ai_edit_suggestions || []).map((s) => (s.id === suggestionId ? updated : s));
    setProject((p) => ({ ...p, ai_edit_suggestions: next }));
    scheduleAutosave(clips, { ai_edit_suggestions: next });
  };
  const acceptAiSuggestion = (id) => setAiEditSuggestionStatus(id, 'accepted');
  const rejectAiSuggestion = (id) => setAiEditSuggestionStatus(id, 'rejected');

  // ── Review / approval / render (identical to Main Editor's Review+Queue) ──
  const toggleApprove = (clipId) => applyClips(clips.map((c) => (c.id === clipId ? { ...c, approved: !c.approved } : c)));
  const batchApproveClips = (outputType) => applyClips(clips.map((c) => (c.output_type === outputType ? { ...c, approved: true } : c)));
  const pollRenderStatus = useCallback((clipId) => {
    if (pollTimersRef.current[clipId]) return;
    pollTimersRef.current[clipId] = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/editor/projects/${projectRef.current.id}/clips/${clipId}/render-status`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? { ...c, ...data.clip } : c)) }));
        if (['completed', 'failed'].includes(data.clip.render_status)) {
          clearInterval(pollTimersRef.current[clipId]); delete pollTimersRef.current[clipId];
          toast[data.clip.render_status === 'completed' ? 'success' : 'error'](
            data.clip.render_status === 'completed' ? `"${data.clip.name}" finished rendering` : `"${data.clip.name}" render failed`
          );
        }
      } catch { /* transient — next tick retries */ }
    }, 3000);
  }, []);
  const submitRender = async (clipId) => {
    await flushAutosave();
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/render`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 503) {
          setGatewayUnavailable(true);
          toast.info('Rendering backend not connected yet (Vultr pending). Your edit is saved.');
        } else {
          toast.error(data.detail?.message || data.detail || 'Could not submit render');
        }
        return;
      }
      setGatewayUnavailable(false);
      setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? { ...c, ...data.clip } : c)) }));
      toast.success('Submitted for rendering');
      pollRenderStatus(clipId);
    } catch { toast.error('Network error submitting render'); }
  };
  const downloadRendered = async (clipId) => {
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/download`, { credentials: 'include' });
      if (!res.ok) { toast.error('Rendered output not available yet'); return; }
      const data = await res.json();
      window.open(data.url, '_blank');
    } catch { toast.error('Could not fetch download link'); }
  };

  useEffect(() => () => { Object.values(pollTimersRef.current).forEach(clearInterval); }, []);

  // ── Autosave — same endpoint/debounce pattern as Main Editor ──
  const pendingSaveRef = useRef(null);
  const [gatewayUnavailable, setGatewayUnavailable] = useState(false);
  const scheduleAutosave = useCallback((clips, extra = {}) => {
    if (!projectRef.current) return;
    pendingSaveRef.current = { clips, ...extra };
    setSaving(true);
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { flushAutosaveRef.current().catch(() => {}); }, 900);
  }, []);
  const flushAutosave = useCallback(async () => {
    clearTimeout(autosaveTimerRef.current);
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!pending || !projectRef.current) { setSaving(false); return; }
    let res;
    try {
      res = await fetch(`${API_URL}/api/editor/projects/${projectRef.current.id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pending),
      });
    } catch (networkErr) {
      setSaving(false);
      // Put the failed edit back so a retry (manual, or the next
      // Production Tools navigation attempt) resends it instead of
      // finding nothing pending and sailing through.
      pendingSaveRef.current = { ...pending, ...(pendingSaveRef.current || {}) };
      toast.error('Autosave failed');
      throw networkErr;
    }
    setSaving(false);
    if (!res.ok) {
      pendingSaveRef.current = { ...pending, ...(pendingSaveRef.current || {}) };
      toast.error('Autosave failed');
      throw new Error('Autosave failed');
    }
  }, []);
  const flushAutosaveRef = useRef(flushAutosave);
  useEffect(() => { flushAutosaveRef.current = flushAutosave; }, [flushAutosave]);
  const applyClips = (nextClips, extra = {}) => {
    setProject((p) => ({ ...p, clips: nextClips, ...extra }));
    scheduleAutosave(nextClips, extra);
  };

  // ── Markers — reads the SAME per-composition localStorage markers Main
  // Editor writes (consistency_editor_markers_{masterComposition.id}).
  // Genuinely shared when this project's Master was marked up in Main
  // Editor; there is still no backend live-session or markers field, so
  // this stays Level 1/2 exactly like it does there. ──
  const [markers, setMarkers] = useState([]);
  const masterCompositionId = project?.master_composition?.id;
  useEffect(() => {
    if (!masterCompositionId) { setMarkers([]); return; }
    try { setMarkers(JSON.parse(localStorage.getItem(`consistency_editor_markers_${masterCompositionId}`) || '[]')); }
    catch { setMarkers([]); }
  }, [masterCompositionId]);

  // ── Client-side-only variant grouping metadata (Level 1/2) — see
  // completion report: the backend EditorClip model has no
  // variant_group_id/platform fields, and uses extra="ignore", so this
  // cannot be sent through the real clip payloads without being silently
  // dropped. Every clip it describes is still a 100% real, independently
  // approvable EditorClip record; only the "these N clips are siblings of
  // one source moment" grouping is local-only until Level 3. ──
  const variantStorageKey = project?.id ? `consistency_clip_variants_${project.id}` : null;
  const [variantMeta, setVariantMeta] = useState({});
  useEffect(() => {
    if (!variantStorageKey) { setVariantMeta({}); return; }
    try { setVariantMeta(JSON.parse(localStorage.getItem(variantStorageKey) || '{}')); } catch { setVariantMeta({}); }
  }, [variantStorageKey]);
  const persistVariantMeta = (next) => {
    setVariantMeta(next);
    if (variantStorageKey) localStorage.setItem(variantStorageKey, JSON.stringify(next));
  };

  // ── Stories (section 7/8) — a Story is the shared asset-type concept,
  // not a new Production Tool: here it is a local sequence of EXISTING
  // clips/videos/images (never duplicated, never re-uploaded). Like
  // variant grouping above, there is no backend "Story" model, so this
  // whole feature is Level 1/2 local state until a real endpoint exists. ──
  const storyStorageKey = project?.id ? `consistency_clip_stories_${project.id}` : null;
  const [stories, setStories] = useState([]);
  useEffect(() => {
    if (!storyStorageKey) { setStories([]); return; }
    try { setStories(JSON.parse(localStorage.getItem(storyStorageKey) || '[]')); } catch { setStories([]); }
  }, [storyStorageKey]);
  const persistStories = (next) => { setStories(next); if (storyStorageKey) localStorage.setItem(storyStorageKey, JSON.stringify(next)); };

  const [activeStoryId, setActiveStoryId] = useState(null);
  const [activeStoryVariantId, setActiveStoryVariantId] = useState(null);
  const [activeStoryPageIdx, setActiveStoryPageIdx] = useState(0);
  const [stripView, setStripView] = useState('clips'); // 'clips' | 'stories'
  const activeStory = stories.find((s) => s.id === activeStoryId) || null;
  const activeStoryVariant = activeStory?.variants.find((v) => v.id === activeStoryVariantId) || null;
  const activeStoryPage = activeStoryVariant?.pages[activeStoryPageIdx] || null;

  const updateStory = (storyId, partial) => persistStories(stories.map((s) => (s.id === storyId ? { ...s, ...partial } : s)));
  const updateStoryVariant = (partial) => {
    if (!activeStory || !activeStoryVariant) return;
    updateStory(activeStory.id, { variants: activeStory.variants.map((v) => (v.id === activeStoryVariant.id ? { ...v, ...partial } : v)) });
  };
  const selectStory = (storyId) => {
    const s = stories.find((ss) => ss.id === storyId);
    if (!s) return;
    setActiveStoryId(storyId); setActiveStoryVariantId(s.variants[0]?.id || null); setActiveStoryPageIdx(0);
  };
  const createStory = (platforms) => {
    if (!platforms.length) { toast.error('Pick at least one supported platform'); return; }
    const variants = platforms.map((platform) => ({ id: newId(), platform, aspectRatio: '9:16', approvalStatus: 'draft', pages: [] }));
    const story = { id: newId(), name: `Story ${stories.length + 1}`, createdAt: new Date().toISOString(), variants };
    persistStories([story, ...stories]);
    setActiveStoryId(story.id); setActiveStoryVariantId(variants[0].id); setActiveStoryPageIdx(0); setStripView('stories');
    toast.success(`Created "${story.name}" with ${platforms.length} platform variant${platforms.length === 1 ? '' : 's'}`);
  };
  const deleteStory = (storyId) => {
    const next = stories.filter((s) => s.id !== storyId);
    persistStories(next);
    if (activeStoryId === storyId) { setActiveStoryId(null); setActiveStoryVariantId(null); setActiveStoryPageIdx(0); }
  };
  const selectStoryVariant = (variantId) => {
    const v = activeStory?.variants.find((vv) => vv.id === variantId);
    if (!v) return;
    setActiveStoryVariantId(variantId); setActiveStoryPageIdx(0);
  };
  const addStoryPage = (page) => { if (!activeStoryVariant) return; updateStoryVariant({ pages: [...activeStoryVariant.pages, page] }); setActiveStoryPageIdx(activeStoryVariant.pages.length); };
  const addStoryPageFromClip = (clipId) => { const c = clips.find((cc) => cc.id === clipId); if (!c) return; addStoryPage({ id: newId(), kind: 'clip', refId: clipId, label: c.name }); };
  const addStoryPageFromVideo = (sourceId) => { const s = siblingSources.find((ss) => ss.id === sourceId); if (!s) return; addStoryPage({ id: newId(), kind: 'video', refId: sourceId, label: s.title || 'Video' }); };
  const addStoryPageFromImageUrl = (url) => { if (!url.trim()) return; addStoryPage({ id: newId(), kind: 'image', refId: url.trim(), label: 'Image' }); };
  const removeStoryPage = (pageId) => {
    if (!activeStoryVariant) return;
    const next = activeStoryVariant.pages.filter((p) => p.id !== pageId);
    updateStoryVariant({ pages: next });
    setActiveStoryPageIdx((i) => Math.min(i, Math.max(0, next.length - 1)));
  };
  const reorderStoryPage = (pageId, dir) => {
    if (!activeStoryVariant) return;
    const idx = activeStoryVariant.pages.findIndex((p) => p.id === pageId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= activeStoryVariant.pages.length) return;
    const next = [...activeStoryVariant.pages];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    updateStoryVariant({ pages: next });
  };
  const setStoryVariantStatus = (variantId, status) => {
    if (!activeStory) return;
    updateStory(activeStory.id, { variants: activeStory.variants.map((v) => (v.id === variantId ? { ...v, approvalStatus: status } : v)) });
  };


  const switchSource = (id) => navigate(`/app/create/clips/${id}${emptyStateProjectId ? `?projectId=${emptyStateProjectId}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}` : ''}`);

  const emptyStateUploadLink = emptyStateProjectId ? `/app/create/workspace/project/${emptyStateProjectId}/upload` : '/app/create/content-studio';
  const emptyStateLibraryLink = emptyStateProjectId ? `/app/create/content-studio?projectId=${emptyStateProjectId}` : '/app/create/content-studio';

  const variantGroupSiblings = selectedClip && variantMeta[selectedClip.id]?.variantGroupId
    ? clips.filter((c) => variantMeta[c.id]?.variantGroupId === variantMeta[selectedClip.id].variantGroupId)
    : [];

  // Shared trim/retrim editor — used by both the auto-opened Properties
  // panel (selecting a clip) and the dedicated "Edit" category so there
  // is exactly one trim implementation, not two competing ones.
  const renderTrimEditor = () => {
    if (!selectedClip) {
      return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="edit-panel-empty">Select a clip (from the Clips category or the strip below) to trim it.</p>;
    }
    if (selectedClip.master_composition_slice) {
      return (
        <div className="space-y-3" data-testid="master-retrim-panel">
          <p className="text-[11px] text-[#8A93A6]">This clip was cut from the approved Master (v{selectedClip.source_master_version}). Adjust its range and it will be re-sliced from that same approved version — never a draft.</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-[#8A93A6] mb-1 block">Start (s)</label>
              <Input type="number" step="0.1" value={clipRangeDraft.start} onChange={(e) => setClipRangeDraft((r) => ({ ...r, start: Number(e.target.value) }))} className="h-8 text-xs bg-white/5 border-white/10" data-testid="master-retrim-start-input" />
            </div>
            <div>
              <label className="text-[11px] text-[#8A93A6] mb-1 block">End (s)</label>
              <Input type="number" step="0.1" value={clipRangeDraft.end} onChange={(e) => setClipRangeDraft((r) => ({ ...r, end: Number(e.target.value) }))} className="h-8 text-xs bg-white/5 border-white/10" data-testid="master-retrim-end-input" />
            </div>
          </div>
          <Button
            size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90"
            disabled={clipRangeDraft.end <= clipRangeDraft.start}
            onClick={() => retrimMasterClip(selectedClip.id, clipRangeDraft.start, clipRangeDraft.end, selectedClip.target_aspect_ratio)}
            data-testid="master-retrim-apply-button"
          >
            Apply New Range
          </Button>
        </div>
      );
    }
    return <EditorClipTrimControls clip={selectedClip} duration={effectiveDuration} currentTime={currentTime} onChange={updateSelectedClip} onSplit={splitSelectedClip} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center" data-testid="clips-workspace-loading">
        <Loader2 className="h-6 w-6 text-[#3A7BFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pb-10" data-testid="clips-workspace">
      <div className="container mx-auto p-6 max-w-[1600px]">
        {/* Breadcrumb + header */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-[#8A93A6] mb-2">
              <Link to="/app/create" className="hover:text-white">Create</Link><span className="opacity-50">/</span>
              {contentSource?.project_id || returnTo ? (
                <>
                  <Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span className="opacity-50">/</span>
                  <Link
                    to={contentSource?.project_id ? `/app/create/workspace/project/${contentSource.project_id}` : returnTo}
                    className="hover:text-white"
                    data-testid="clips-back-to-project-link"
                  >
                    Project
                  </Link>
                  <span className="opacity-50">/</span>
                </>
              ) : null}
              <span className="text-[#C0C7D4]">Clips</span>
              <span className="opacity-50">/</span>
              <Link
                to={`/app/create/content-studio?${contentSource?.project_id ? `projectId=${contentSource.project_id}&` : emptyStateProjectId ? `projectId=${emptyStateProjectId}&` : ''}returnTo=${encodeURIComponent(currentSourceId ? `/app/create/clips/${currentSourceId}` : (returnTo || '/app/create/workspace'))}`}
                className="hover:text-white"
                data-testid="clips-media-library-link"
              >
                Media Library
              </Link>
            </div>
            <h1 className="text-2xl font-bold text-white truncate" data-testid="clips-workspace-title">{contentSource?.title || 'Clips'}</h1>
            {siblingSources.length > 1 && (
              <div className="mt-2 flex items-center gap-2">
                <Video className="h-3.5 w-3.5 text-[#8A93A6]" />
                <Select value={currentSourceId} onValueChange={switchSource}>
                  <SelectTrigger className="h-7 w-56 text-xs bg-white/5 border-white/10" data-testid="clips-source-select">
                    <SelectValue placeholder="Select video" />
                  </SelectTrigger>
                  <SelectContent>
                    {siblingSources.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`clips-source-option-${s.id}`}>{s.title || s.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {project && <span className="text-xs text-[#8A93A6]" data-testid="clips-save-indicator">{saving ? 'Saving…' : 'Saved'}</span>}
            <ProductionToolsDropdown
              currentTool="clips"
              creatorId={contentSource?.creator_id || localStorage.getItem('activeCreatorId')}
              projectId={contentSource?.project_id || emptyStateProjectId}
              assetId={currentSourceId}
              returnTo={currentSourceId ? `/app/create/clips/${currentSourceId}` : (returnTo || '/app/create/workspace')}
              onBeforeNavigate={flushAutosave}
            />
            <Link to="/app/create/clips-review" className="text-xs text-[#8A93A6] hover:text-white underline underline-offset-2" data-testid="legacy-clips-review-link">
              Legacy Clips Review
            </Link>
          </div>
        </div>

        <div className="space-y-3" data-testid="clips-workspace-shell">
          <div className="flex gap-3 items-stretch">
            <EditorToolSidebar
              categories={CLIP_TOOL_CATEGORIES}
              activeCategory={viewMode === 'properties' ? null : toolCategory}
              onSelectCategory={handleSelectCategory}
              collapsed={sidebarCollapsed}
              onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
            />

            {!sidebarCollapsed && (
              <div className="w-72 shrink-0 lg:h-[480px] overflow-y-auto" data-testid="clips-tool-panel">
                {viewMode === 'properties' && selectedClip ? (
                  <div className="glass rounded-xl p-3 lg:h-[480px] flex flex-col" data-testid="clips-properties-panel">
                    <button
                      onClick={handleBackFromProperties}
                      className="flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white mb-2 shrink-0"
                      data-testid="clips-properties-back-button"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />Back to {CLIP_CATEGORY_LABELS[lastCategoryBeforeProperties] || 'tools'}
                    </button>
                    <div className="overflow-y-auto flex-1 pr-1 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-white truncate">{selectedClip.name}</p>
                        <p className="text-[11px] text-[#8A93A6]">{selectedClip.output_type === 'lf' ? 'Long-form' : 'Short-form'} · {selectedClip.target_aspect_ratio || '—'}{variantMeta[selectedClip.id]?.platform ? ` · ${platformLabel(variantMeta[selectedClip.id].platform)}` : ''}</p>
                      </div>
                      {renderTrimEditor()}
                      <div className="grid grid-cols-2 gap-1.5 pt-1 border-t border-white/10">
                        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => handleSelectCategory('reframe')} data-testid="properties-jump-reframe">Reframe</Button>
                        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => handleSelectCategory('captions')} data-testid="properties-jump-captions">Captions</Button>
                        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => handleSelectCategory('brand')} data-testid="properties-jump-brand">Brand Kit</Button>
                        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => handleSelectCategory('review')} data-testid="properties-jump-review">Review</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="glass rounded-xl p-4 lg:h-[480px] overflow-y-auto" data-testid={`clips-panel-${toolCategory}`}>
                    {toolCategory === 'media' && (
                      <EditorMediaPanel
                        sources={siblingSources} parentAssetId={contentSource?.parent_asset_id}
                        onQuickAdd={(s) => switchSource(s.id)} context="clips"
                        uploadTo={emptyStateUploadLink} libraryTo={emptyStateLibraryLink}
                        startFromVideos={emptyStateVideos} startFromLoading={emptyStateVideosLoading} onStartFromVideo={switchSource}
                      />
                    )}

                    {toolCategory === 'generate' && (
                      !currentSourceId ? (
                        <EditorPlaceholderPanel icon={Wand2} title="Pick a video first" body="Choose a video from Media to generate clips manually, from markers or with AI-assisted Best Moments." />
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center gap-1.5 rounded-lg bg-white/5 p-1">
                            {['lf', 'sf'].map((t) => (
                              <button
                                key={t} onClick={() => setActiveOutputType(t)}
                                className={`flex-1 text-xs py-1.5 rounded-md ${activeOutputType === t ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`}
                                data-testid={`generate-output-type-${t}`}
                              >
                                {t === 'lf' ? 'Long-form' : 'Short-form'}
                              </button>
                            ))}
                          </div>
                          <EditorClipGenerationPanel
                            disabled={!masterApproved} approvedDuration={approvedDuration}
                            onGenerate={(payload) => generateClips({ ...payload, output_type: activeOutputType })}
                            generating={generatingClips}
                            acceptedHighlightSuggestions={(project?.ai_edit_suggestions || []).filter((s) => s.kind === 'highlight' && s.status === 'accepted')}
                            onGenerateFromSuggestions={(ids) => generateClipsFromSuggestions(ids, activeOutputType)}
                            markers={markers}
                          />
                          <div className="pt-1 border-t border-white/10">
                            <ClipVariantGenerator
                              disabled={!masterApproved} approvedDuration={approvedDuration}
                              onGenerateVariants={generateVariants} generating={generatingVariants}
                            />
                          </div>
                          <div className="pt-1 border-t border-white/10">
                            <p className="text-xs font-medium text-white mb-2">AI-Assisted (Best Moments, etc.)</p>
                            <EditorAiToolsPanel
                              siblingSources={siblingSources.length ? siblingSources : (contentSource ? [contentSource] : [])}
                              suggestions={project?.ai_edit_suggestions || []}
                              onRequestAnalysis={requestAiAnalysis} onAcceptSuggestion={acceptAiSuggestion} onRejectSuggestion={rejectAiSuggestion}
                            />
                          </div>
                          <div className="pt-1 border-t border-white/10">
                            <StoryCreatePanel onCreateStory={createStory} />
                          </div>
                        </div>
                      )
                    )}

                    {toolCategory === 'clips' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-1.5 rounded-lg bg-white/5 p-1">
                          <button onClick={() => setStripView('clips')} className={`flex-1 text-xs py-1.5 rounded-md ${stripView === 'clips' ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid="clips-strip-view-clips">Clips</button>
                          <button onClick={() => setStripView('stories')} className={`flex-1 text-xs py-1.5 rounded-md ${stripView === 'stories' ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid="clips-strip-view-stories">Stories ({stories.length})</button>
                        </div>
                        {stripView === 'clips' ? (
                          <>
                            <div className="flex items-center gap-1.5 rounded-lg bg-white/5 p-1">
                              {['lf', 'sf'].map((t) => (
                                <button
                                  key={t} onClick={() => setActiveOutputType(t)}
                                  className={`flex-1 text-xs py-1.5 rounded-md ${activeOutputType === t ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`}
                                  data-testid={`clips-list-output-type-${t}`}
                                >
                                  {t === 'lf' ? 'Long-form' : 'Short-form'} ({clips.filter((c) => c.output_type === t).length})
                                </button>
                              ))}
                            </div>
                            <EditorClipList
                              clips={clipsOfActiveType} selectedClipId={selectedClipId} onSelect={setSelectedClipId}
                              onAdd={() => handleSelectCategory('generate')}
                              onRename={renameClip} onRemove={removeClip} onReorder={reorderClip}
                            />
                          </>
                        ) : (
                          <StoryManagerPanel
                            stories={stories} activeStoryId={activeStoryId} onSelectStory={selectStory} onDeleteStory={deleteStory}
                            activeStory={activeStory} activeStoryVariant={activeStoryVariant} onSelectStoryVariant={selectStoryVariant}
                            clips={clips} siblingSources={siblingSources}
                            onAddPageFromClip={addStoryPageFromClip} onAddPageFromVideo={addStoryPageFromVideo} onAddPageFromImageUrl={addStoryPageFromImageUrl}
                            onRemovePage={removeStoryPage} onReorderPage={reorderStoryPage}
                            activePageIdx={activeStoryPageIdx} onSelectPage={setActiveStoryPageIdx}
                          />
                        )}
                      </div>
                    )}

                    {toolCategory === 'reframe' && (
                      !selectedClip ? (
                        <EditorPlaceholderPanel icon={Crop} title="Select a clip" body="Pick a clip from Clips or the strip below to change which aspect ratio it targets." />
                      ) : (
                        <div className="space-y-3" data-testid="reframe-panel">
                          <p className="text-[11px] text-[#8A93A6]">Current target: <span className="text-white">{selectedClip.target_aspect_ratio || '—'}</span></p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {['16:9', '9:16', '1:1', '4:5'].map((ar) => (
                              <Button
                                key={ar} size="sm" variant={selectedClip.target_aspect_ratio === ar ? 'default' : 'outline'}
                                className={selectedClip.target_aspect_ratio === ar ? 'bg-[#3A7BFF] hover:bg-[#3A7BFF]/90' : 'border-white/10'}
                                onClick={() => (selectedClip.master_composition_slice
                                  ? retrimMasterClip(selectedClip.id, selectedClip.master_range_start_seconds, selectedClip.master_range_end_seconds, ar)
                                  : updateSelectedClip({ target_aspect_ratio: ar }))}
                                data-testid={`reframe-ratio-${ar.replace(':', '-')}`}
                              >
                                {ar}
                              </Button>
                            ))}
                          </div>
                          <p className="text-[10px] text-[#555E6E]">Changing a Master-derived clip's ratio re-slices it server-side from the approved Master; a manually-added clip just updates its target.</p>
                        </div>
                      )
                    )}

                    {toolCategory === 'captions' && (
                      <EditorCaptionsPanel
                        captions={selectedClip?.captions}
                        duration={selectedClip ? selectedClip.out_point - selectedClip.in_point : 0}
                        currentTime={selectedClip ? currentTime - selectedClip.in_point : 0}
                        onAdd={addCaption} onUpdate={updateCaption} onRemove={removeCaption}
                      />
                    )}

                    {toolCategory === 'edit' && (
                      <div data-testid="edit-panel">{renderTrimEditor()}</div>
                    )}

                    {toolCategory === 'brand' && (
                      <EditorBrandKitPanel
                        clip={selectedClip} brandkits={brandkits} defaultBrandKitId={effectiveBrandKit?.id}
                        onApply={applyBrandKit} onApplyToAllOfType={applyBrandKitToAllOfType}
                      />
                    )}

                    {toolCategory === 'templates' && (
                      <EditorTemplatesPanel creatorId={contentSource?.creator_id} templateKind="Clip Template" projectId={contentSource?.project_id} episodeId={currentSourceId} />
                    )}

                    {toolCategory === 'review' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-1.5 rounded-lg bg-white/5 p-1">
                          <button onClick={() => setStripView('clips')} className={`flex-1 text-xs py-1.5 rounded-md ${stripView === 'clips' ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid="review-strip-view-clips">Clips</button>
                          <button onClick={() => setStripView('stories')} className={`flex-1 text-xs py-1.5 rounded-md ${stripView === 'stories' ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid="review-strip-view-stories">Stories</button>
                        </div>
                        {stripView === 'clips' ? (
                          <>
                            <EditorClipReviewQueue clips={clipsOfActiveType} onBatchApprove={() => batchApproveClips(activeOutputType)} onSelectClip={setSelectedClipId} />
                            <div className="pt-2 border-t border-white/10">
                              <EditorReviewPanel clips={clipsOfActiveType} onToggleApprove={toggleApprove} onSubmitRender={submitRender} onDownload={downloadRendered} gatewayUnavailable={gatewayUnavailable} />
                            </div>
                          </>
                        ) : (
                          <DesignReviewPanel
                            variants={activeStory?.variants || []} activeVariantId={activeStoryVariantId}
                            onSelectVariant={selectStoryVariant} onCycleStatus={setStoryVariantStatus}
                            onReject={(vid) => setStoryVariantStatus(vid, 'rejected')}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="glass rounded-xl p-3 lg:h-[480px] flex flex-col" data-testid="clips-preview-area">
                {!currentSourceId ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="clips-no-source-prompt">
                    <Film className="h-8 w-8 text-[#555E6E]" />
                    <p className="text-sm text-[#8A93A6] max-w-xs">Pick a video from Media on the left — or upload / browse your Library — to start generating clips. Every other tool stays browseable in the meantime.</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-white/10" asChild data-testid="no-source-upload-button">
                        <Link to={emptyStateUploadLink}><Upload className="h-3.5 w-3.5 mr-1.5" />Upload</Link>
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/10" asChild data-testid="no-source-library-button">
                        <Link to={emptyStateLibraryLink}><Link2 className="h-3.5 w-3.5 mr-1.5" />Browse Library</Link>
                      </Button>
                    </div>
                  </div>
                ) : streamError ? (
                  <div className="flex-1 flex items-center justify-center text-center text-sm text-amber-300 px-6" data-testid="clips-stream-error">{streamError}</div>
                ) : stripView === 'stories' && activeStory ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="story-preview-area">
                    {activeStoryPage ? (
                      <>
                        {activeStoryPage.kind === 'image'
                          ? <img src={activeStoryPage.refId} alt="" className="max-h-[70%] max-w-[70%] rounded-lg border border-white/10 object-contain" />
                          : <Clapperboard className="h-10 w-10 text-[#3A7BFF]" />}
                        <p className="text-sm text-white">{activeStoryPage.label}</p>
                        <p className="text-[11px] text-[#8A93A6]">Page {activeStoryPageIdx + 1} of {activeStoryVariant.pages.length} · {platformLabel(activeStoryVariant.platform)}</p>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="border-white/10" disabled={activeStoryPageIdx === 0} onClick={() => setActiveStoryPageIdx((i) => i - 1)} data-testid="story-preview-prev">Prev</Button>
                          <Button size="sm" variant="outline" className="border-white/10" disabled={activeStoryPageIdx >= activeStoryVariant.pages.length - 1} onClick={() => setActiveStoryPageIdx((i) => i + 1)} data-testid="story-preview-next">Next</Button>
                        </div>
                        <p className="text-[10px] text-[#555E6E] max-w-xs">Page-by-page preview only — full in-sequence video/mixed-media playback is a Level 3 renderer, not built here.</p>
                      </>
                    ) : (
                      <p className="text-sm text-[#8A93A6]">This Story has no pages yet — add one from the Clips category.</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-h-0 bg-black rounded-lg overflow-hidden relative flex items-center justify-center">
                      <video
                        ref={videoRef} src={streamUrl || undefined} className="max-h-full max-w-full"
                        onLoadedMetadata={handleLoadedMetadata} onTimeUpdate={handleTimeUpdate}
                        onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)}
                        data-testid="clips-preview-video"
                      />
                      {activeCaption && (
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1.5 rounded-md max-w-[85%] text-center" data-testid="clips-preview-caption">
                          {activeCaption.text}
                        </div>
                      )}
                      {selectedClip?.master_composition_slice && (
                        <div className="absolute top-2 left-2 bg-black/60 text-[10px] text-[#8A93A6] px-2 py-1 rounded-md" data-testid="clips-preview-approx-note">
                          Approximate preview from source — full Master effects/overlays render in Main Editor
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 pt-2 shrink-0">
                      <button onClick={togglePlay} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0" data-testid="clips-preview-playpause">
                        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                      </button>
                      <input
                        type="range" min={0} max={effectiveDuration || 0} step={0.1} value={currentTime}
                        onChange={(e) => seekTo(Number(e.target.value))}
                        className="flex-1 accent-[#3A7BFF]" data-testid="clips-preview-scrubber"
                      />
                      <span className="text-[11px] text-[#8A93A6] tabular-nums shrink-0">{fmtTime(currentTime)} / {fmtTime(effectiveDuration || 0)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {variantGroupSiblings.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap px-1" data-testid="variant-switcher">
              <span className="text-[11px] text-[#8A93A6] flex items-center gap-1"><Layers className="h-3 w-3" />Variants of this moment:</span>
              {variantGroupSiblings.map((c) => (
                <button
                  key={c.id} onClick={() => setSelectedClipId(c.id)}
                  className={`text-[11px] px-2 py-1 rounded-full border ${c.id === selectedClipId ? 'border-[#3A7BFF] bg-[#3A7BFF]/15 text-white' : 'border-white/10 text-[#8A93A6] hover:text-white'}`}
                  data-testid={`variant-switch-${c.id}`}
                >
                  {platformLabel(variantMeta[c.id]?.platform)}
                </button>
              ))}
              <span className="text-[10px] text-[#555E6E]">Switching keeps each variant's own saved edits — editing one never overwrites another.</span>
            </div>
          )}

          <div className="relative">
            <div
              className="h-2 -mb-1 flex items-center justify-center gap-2 cursor-row-resize group relative z-10"
              onMouseDown={stripCollapsed ? undefined : startStripResize}
              data-testid="clip-strip-resize-handle"
              title={stripCollapsed ? 'Strip collapsed' : 'Drag to resize the clip strip'}
            >
              <div className="h-1 w-16 rounded-full bg-white/10 group-hover:bg-[#3A7BFF]/60 transition-colors" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setStripCollapsed((c) => !c); }}
                className="absolute right-1 -top-1 h-5 w-5 rounded-md bg-white/5 hover:bg-white/10 text-[#8A93A6] hover:text-white flex items-center justify-center"
                title={stripCollapsed ? 'Expand clip strip' : 'Collapse clip strip'}
                data-testid="clip-strip-collapse-toggle"
              >
                {stripCollapsed ? <ChevronsUp className="h-3.5 w-3.5" /> : <ChevronsDown className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div style={stripCollapsed ? { display: 'none' } : { height: stripHeight }} className="glass rounded-xl p-3 overflow-hidden flex flex-col" data-testid="clip-strip">
              {stripView === 'stories' ? (
                !activeStory ? (
                  <p className="text-sm text-[#8A93A6] m-auto" data-testid="story-strip-empty">No Story selected — create or pick one in the Clips category.</p>
                ) : (
                  <>
                    <p className="text-[11px] text-[#8A93A6] mb-2 shrink-0">{activeStory.name} — {activeStoryVariant?.pages.length || 0} page{(activeStoryVariant?.pages.length || 0) === 1 ? '' : 's'} ({platformLabel(activeStoryVariant?.platform)})</p>
                    <div className="flex-1 overflow-x-auto flex gap-2 pb-1">
                      {(activeStoryVariant?.pages || []).length === 0 ? (
                        <p className="text-sm text-[#8A93A6] m-auto">Add a page from existing clips, videos or an image URL in the Clips category.</p>
                      ) : activeStoryVariant.pages.map((p, idx) => (
                        <button
                          key={p.id} onClick={() => setActiveStoryPageIdx(idx)}
                          className={`shrink-0 w-32 text-left p-2 rounded-lg border ${idx === activeStoryPageIdx ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`}
                          data-testid={`story-strip-page-${p.id}`}
                        >
                          <span className="text-xs text-white truncate block">{idx + 1}. {p.label}</span>
                          <span className="text-[10px] text-[#8A93A6] capitalize">{p.kind}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )
              ) : (
                <>
                  <p className="text-[11px] text-[#8A93A6] mb-2 shrink-0">{allClipsSorted.length} clip{allClipsSorted.length === 1 ? '' : 's'} in this project</p>
                  <div className="flex-1 overflow-x-auto flex gap-2 pb-1">
                    {allClipsSorted.length === 0 ? (
                      <p className="text-sm text-[#8A93A6] m-auto" data-testid="clip-strip-empty">No clips yet — use Generate to create your first one.</p>
                    ) : allClipsSorted.map((c) => (
                      <button
                        key={c.id} onClick={() => setSelectedClipId(c.id)}
                        className={`shrink-0 w-36 text-left p-2 rounded-lg border ${c.id === selectedClipId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`}
                        data-testid={`clip-strip-item-${c.id}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          {c.approved ? <CheckCircle2 className="h-3 w-3 text-green-400 shrink-0" /> : <Circle className="h-3 w-3 text-[#555E6E] shrink-0" />}
                          <span className="text-xs text-white truncate">{c.name}</span>
                        </div>
                        <p className="text-[10px] text-[#8A93A6]">{fmtTime(c.in_point)}–{fmtTime(c.out_point)} · {c.output_type?.toUpperCase()}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {c.master_composition_slice && <Clapperboard className="h-2.5 w-2.5 text-[#3A7BFF]" />}
                          {variantMeta[c.id]?.platform && <span className="text-[9px] text-[#8A93A6]">{platformLabel(variantMeta[c.id].platform)}</span>}
                          {c.render_status === 'completed' && <Sparkles className="h-2.5 w-2.5 text-amber-400" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
